import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getBattery } from "./api";
import "./App.css";

const formatDate = (timestamp) =>
	new Date(timestamp * 1000).toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});

const isExpired = (timestamp) => {
  const expiry = new Date(timestamp * 1000);
  expiry.setHours(23, 59, 59, 999);
  return expiry < new Date();
};

const renderValue = (value, fallback = "Unknown") => {
	if (value == null) {
		return fallback;
	}

	if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
		return value.toString();
	}

	return (
		<span style={{ color: "#a60" }}>
			Unexpected data format
		</span>
	);
};

function SingleBatteryPage() {
	const { gbuid } = useParams();
	const [battery, setBattery] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		setLoading(true);
		setError(null);

		getBattery(gbuid).then(setBattery)
			.catch(() => setError("Battery not found in database."))
			.finally(() => setLoading(false));
	}, [gbuid]);

	// Seems to show up quite a bit, so maybe it should be improved.
	if (loading) return <p>Loading...</p>;
	if (error) return <p>{error}</p>;

	return (
		<>
			<nav>
				<img src="/WT-Full-Logo-Blue@0.5.png" alt="WaveTech Logo" />
				<div className="nav-links">
					<a href="https://www.wavetech.com">WaveTech</a>
					<a href="https://www.wavetech.com/contact">Contact</a>
				</div>
			</nav>

			<main style={{ maxWidth: "800px", margin: "0 auto", marginBottom: "40px" }}>
				<h1>Battery Information</h1>

				<div style={{ display: "flex", gap: "30px", alignItems: "flex-start" }}>
					<div style={{ flex: 1 }}>
						<div>
							<strong>Global ID:</strong> {battery.gbuid}
						</div>

						{battery.nameplate && (
							<>
								<h2>Specifications</h2>

								<table>
									<tbody>
										<tr>
											<th align="right">{battery.nameplate.capacityTitle}</th>
											<td>{renderValue(battery.nameplate.capacityValue)}</td>
										</tr>
										<tr>
											<th align="right">{battery.nameplate.nomVoltageTitle}</th>
											<td>{renderValue(battery.nameplate.nomVoltageValue)}</td>
										</tr>
										<tr>
											<th align="right">{battery.nameplate.standbyUseTitle}</th>
											<td>{renderValue(battery.nameplate.standbyUseValue)}</td>
										</tr>
										<tr>
											<th align="right">{battery.nameplate.cycleUseTitle}</th>
											<td>{renderValue(battery.nameplate.cycleUseValue)}</td>
										</tr>
										<tr>
											<th align="right">{battery.nameplate.maxInitialCurrentTitle}</th>
											<td>{renderValue(battery.nameplate.maxInitialCurrentValue)}</td>
										</tr>
										<tr>
											<th align="right">CO₂ Recycled</th>
											<td>{renderValue(battery.nameplate.co2TitlePercentage)}%</td>
										</tr>
									</tbody>
								</table>
							</>
						)}

						{battery.warranty?.length > 0 && (
							<>
								<h2>Warranty Info</h2>

								{battery.warranty.map((w, index) => (
									<div key={index} className="container" style={{ width: "280px", alignItems: "center" }}>
										<div>
											<strong>{w.type}</strong>
											<br />
											{w.warrantor}
											<br />
											{isExpired(w.expDate) ? "Expired" : "Expires"}: {formatDate(w.expDate)}
										</div>

										{w.image && (
											<img
												src={`/visuals/${w.image}.png`}
												style={{ height: "72px", width: "auto", marginLeft: "auto" }}
											/>
										)}
									</div>
								))}
							</>
						)}

						{battery.nameplate && (
							<>
								<h2>Service Provider</h2>

								<table>
									<tbody>
										<tr>
											<th align="right">Name</th>
											<td>{renderValue(battery.nameplate.localContactName)}</td>
										</tr>
										<tr>
											<th align="right">Website</th>
											<td>
												<a
													href={`https://${battery.nameplate.localContactWebsite}`}
													target="_blank"
													rel="noopener noreferrer"
												>
													{renderValue(battery.nameplate.localContactWebsite)}
												</a>
											</td>
										</tr>
									</tbody>
								</table>
							</>
						)}

						{battery.events?.length > 0 && (
							<>
								<h2>Service Log</h2>

								{battery.events.map((event, index) => (
									<div key={index} className="container" style={{ flexDirection: "column"}}>
										<div>
											<span style={{ textDecoration: "underline", fontWeight: "bold" }}>
												{`${formatDate(event.date)}: ${event.type}`}
											</span>
										</div>

										<table>
											<tbody>
												<tr>
													<th align="right">Local ID</th>
													<td>{renderValue(event.lbuid)}</td>
												</tr>

												{event.result && Object.entries(event.result).map(([key, value]) => {
													if (value == null) return null;

													const labels = {
														capacity: "Capacity",
														ir: "IR",
														classification: "Classification",
													};

													let renderedValue = renderValue(value);

													switch (key) {
														case "capacity":
															renderedValue = `${renderValue(value)}`;
															break;
														
														case "ir":
															renderedValue = `${renderValue(value)} mΩ`;
															break;

														case "classification": {
															const tooltipDict = {
																"AC-1": "Certified for original OEM applications",
																"AC-2": "Certified for original OEM applications, with rerated runtime",
																"AC-3": "Certified for secondary (second life) applications",
																"AC-4": "Unfit for certification (Recycle/Scrap)",
															};

															renderedValue = (
																<span className="tooltip-trigger">
																	{value}
																	<span className="tooltip">{tooltipDict[value]}</span>
																</span>
															);
															break;
														}
													}

													return (
														<tr key={key}>
															<th align="right">{labels[key] ?? key}</th>
															<td>{renderedValue}</td>
														</tr>
													);
												})}

												{event.notes && (
													<tr>
														<th align="right">Notes</th>
														<td>{renderValue(event.notes)}</td>
													</tr>
												)}
											</tbody>
										</table>
									</div>
								))}
							</>
						)}

						{battery.oem && (
							<>
								<h2>Original Manufacturer</h2>

								<table>
									<tbody>
										<tr>
											<th align="right">Manufacturer</th>
											<td>{renderValue(battery.oem.name)}</td>
										</tr>
										<tr>
											<th align="right">Model</th>
											<td>{renderValue(battery.oem.mdl)}</td>
										</tr>
									</tbody>
								</table>
							</>
						)}
					</div>

					{battery.visual?.image && (
						<div>
							<img
								src={`/visuals/${battery.visual.image}.png`}
								style={{ width: "220px", height: "auto" }}
							/>
						</div>
					)}
				</div>
			</main>
		</>
	);
}

export default SingleBatteryPage;